import { useEffect, useRef, useState } from "react";
import {
  ShareNetwork,
  WhatsappLogo,
  TwitterLogo,
  FacebookLogo,
  PinterestLogo,
  TelegramLogo,
  LinkSimple,
  EnvelopeSimple,
  Check,
} from "@phosphor-icons/react";

export default function ShareButton({ url, title, description, image }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const shareUrl = url || (typeof window !== "undefined" ? window.location.href : "");
  const shareTitle = title || "";
  const shareText = description ? `${shareTitle} — ${description}` : shareTitle;

  const enc = (s) => encodeURIComponent(s || "");

  const links = [
    {
      key: "whatsapp",
      label: "WhatsApp",
      Icon: WhatsappLogo,
      href: `https://wa.me/?text=${enc(`${shareText} ${shareUrl}`)}`,
    },
    {
      key: "twitter",
      label: "X (Twitter)",
      Icon: TwitterLogo,
      href: `https://twitter.com/intent/tweet?url=${enc(shareUrl)}&text=${enc(shareText)}`,
    },
    {
      key: "facebook",
      label: "Facebook",
      Icon: FacebookLogo,
      href: `https://www.facebook.com/sharer/sharer.php?u=${enc(shareUrl)}`,
    },
    {
      key: "pinterest",
      label: "Pinterest",
      Icon: PinterestLogo,
      href: `https://pinterest.com/pin/create/button/?url=${enc(shareUrl)}&media=${enc(image || "")}&description=${enc(shareText)}`,
    },
    {
      key: "telegram",
      label: "Telegram",
      Icon: TelegramLogo,
      href: `https://t.me/share/url?url=${enc(shareUrl)}&text=${enc(shareText)}`,
    },
    {
      key: "email",
      label: "Email",
      Icon: EnvelopeSimple,
      href: `mailto:?subject=${enc(shareTitle)}&body=${enc(`${shareText}\n\n${shareUrl}`)}`,
    },
  ];

  const handleNativeOrToggle = async () => {
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({ title: shareTitle, text: shareText, url: shareUrl });
        return;
      } catch {
        // User cancelled or failed → fall back to dropdown
      }
    }
    setOpen((o) => !o);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // ignore
    }
  };

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        data-testid="share-product-button"
        onClick={handleNativeOrToggle}
        className="inline-flex items-center gap-2 h-10 px-5 rounded-full border-2 border-[#E01A23] text-[#E01A23] hover:bg-[#E01A23] hover:text-white transition-colors text-sm"
        style={{ fontFamily: "'Courier New', Courier, monospace" }}
      >
        <ShareNetwork size={16} weight="bold" />
        share
      </button>

      {open && (
        <div
          data-testid="share-menu"
          className="absolute z-30 mt-2 left-0 w-64 bg-[#F5E6A0] border border-[#E01A23]/30 rounded-2xl shadow-xl p-3"
          style={{ fontFamily: "'Courier New', Courier, monospace" }}
        >
          <p className="text-xs text-[#555] tracking-wider px-2 pb-2">share this product</p>
          <div className="grid grid-cols-3 gap-2">
            {links.map(({ key, label, Icon, href }) => (
              <a
                key={key}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`share-${key}`}
                className="flex flex-col items-center justify-center gap-1 p-2 rounded-xl text-[#3a3a3a] hover:text-[#E01A23] hover:bg-[#EBD988] transition-colors"
              >
                <Icon size={22} weight="regular" />
                <span className="text-[10px]">{label}</span>
              </a>
            ))}
          </div>
          <button
            type="button"
            data-testid="share-copy-link"
            onClick={copyLink}
            className="mt-2 w-full flex items-center justify-center gap-2 h-9 rounded-xl border border-[#E01A23]/30 text-[#1A1A1A] hover:bg-[#EBD988] transition-colors text-xs"
          >
            {copied ? (
              <>
                <Check size={14} weight="bold" /> link copied
              </>
            ) : (
              <>
                <LinkSimple size={14} weight="bold" /> copy link
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}
